import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { accessToPartnershipData } from "utils/influencer";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import { StripeInvoiceLineItem, TabOptions } from "../schema";
import styles from "../styles";
import { useCheckout } from "../useCheckout";
import PartnershipDataBundle from "./CrossSells/PartnershipDataBundle";
import VaAddOn from "./CrossSells/VaAddOn";
import PromotionalCode from "./PromotionalCode";

interface Props {
  displayName: string | undefined;
  productId: string;
  hideVaBundle?: boolean;
}

const InvoicePreview = ({ displayName, productId, hideVaBundle }: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { currentOrg } = useContext(OrganizationUserContext);
  const { tab, setTab, subscription, isVaPlan } =
    useContext(SubscriptionContext);
  const { setAlert } = useContext(AlertContext);
  const { applyAddOnLoading, invoice, requiresBillingAddress, setPlan, plan } =
    useContext(CheckoutContext);
  const {
    applyAddOnItem,
    removePromoCode,
    getQuantityOfAddOnFromInvoice,
    updatePreviewInvoice,
  } = useCheckout();

  // Variables
  const hasDiscount = invoice?.discounts?.find(
    (x) => x.promotion_code && x.promotion_code?.length > 0,
  );
  const negativeBalance =
    invoice?.starting_balance && invoice?.starting_balance < 0;
  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
    isVaPlan,
  );
  const displayPartnershipBundle =
    !hasAccessToPartnershipData &&
    (plan === Plans.PRO || plan === Plans.PRO_YEARLY);
  const displayVaBundle =
    (plan === Plans.VA || plan === Plans.VA_YEARLY) && !hideVaBundle;
  const templateQuantity = getQuantityOfAddOnFromInvoice(
    AddOnItemName.pitching_templates,
  );

  const [openAddOns, setOpenAddOns] = useState(false);

  const handleOpenAddOns = () => {
    trackEvent("Subscriptions Page - Add Ons Button Clicked");
    setOpenAddOns(true);
  };

  const switchTab = () => {
    if (!plan || !(plan in TabOptions)) {
      return;
    }
    if (tab === SubscriptionTab.monthly) {
      setTab(SubscriptionTab.yearly);
    } else {
      setTab(SubscriptionTab.monthly);
    }
    const alternativePlan = TabOptions[plan as keyof typeof TabOptions];
    updatePreviewInvoice({
      plan: alternativePlan,
    });
    setPlan(alternativePlan);
  };

  const getItemName = (row: StripeInvoiceLineItem) => {
    return row?.metadata?.item_name;
  };

  const renderDescription = (description: string) => {
    if (description.startsWith("1 × ")) {
      return description.slice(4);
    }
    return description;
  };

  const renderTime = (start: number, end: number) => {
    return (
      <Box sx={{ fontSize: 10, color: grey[500] }}>
        {` ${formatEpochTimestamp({
          epochTimestamp: start,
          addYear: true,
        })} - ${formatEpochTimestamp({
          epochTimestamp: end,
          addYear: true,
        })}`}
      </Box>
    );
  };

  const renderBundle = () => {
    if (displayVaBundle) {
      return (
        <Box>
          {[
            AddOnItemName.pitching_templates,
            AddOnItemName.niche,
            AddOnItemName.support_call,
          ].map((addOnItemName) => (
            <Box key={addOnItemName} sx={{ mt: 2 }}>
              <VaAddOn addOnItemName={addOnItemName} />
            </Box>
          ))}
        </Box>
      );
    }

    if (displayPartnershipBundle) {
      return <PartnershipDataBundle />;
    }
    return <></>;
  };

  useEffect(() => {
    const isYearlyPlan =
      plan && [Plans.VA_YEARLY, Plans.PRO_YEARLY]?.includes(plan as Plans);
    if (isYearlyPlan && tab !== SubscriptionTab.yearly) {
      setTab(SubscriptionTab.yearly);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mt: 3 }}>
      {plan && plan in TabOptions && (
        <Grid container sx={styles.tabs}>
          <Button
            sx={[
              styles.tab,
              tab === SubscriptionTab.monthly ? styles.active : {},
            ]}
            onClick={switchTab}
          >
            Monthly
          </Button>
          <Button
            sx={[
              styles.tab,
              tab === SubscriptionTab.yearly ? styles.active : {},
            ]}
            onClick={switchTab}
          >
            Yearly <Box sx={styles.annotation}>(Save 25%)</Box>
          </Button>
        </Grid>
      )}
      <Table size="small" sx={{ mt: 2 }}>
        <TableBody>
          {invoice?.lines?.data?.map((row, index) => (
            <TableRow key={index}>
              <TableCell component="th" scope="row">
                {renderDescription(row.description)}
                {plan === AddOnItemName.custom_email && index === 1 && (
                  <ul
                    style={{
                      color: grey[500],
                      margin: 0,
                      marginTop: "4px",
                      paddingLeft: "20px",
                    }}
                  >
                    <li>Gmail inbox with your domain</li>
                    <li>Monthly hosting</li>
                    <li>Deliverability setup (SPF, DKIM, DMARC)</li>
                  </ul>
                )}

                {displayName &&
                  row?.period?.start &&
                  row?.period?.end &&
                  row?.description?.includes(displayName) &&
                  renderTime(row?.period?.start, row?.period?.end)}

                {displayName &&
                  row?.metadata?.start &&
                  row?.metadata?.end &&
                  renderTime(
                    Number(row?.metadata?.start),
                    Number(row?.metadata?.end),
                  )}

                {getItemName(row) && (
                  <Grid item xs={12}>
                    <LoadingButton
                      size="small"
                      loading={applyAddOnLoading === getItemName(row)}
                      sx={{ fontSize: 10, color: "warning.main", ml: -0.5 }}
                      onClick={async () => {
                        if (
                          getItemName(row) === AddOnItemName.niche &&
                          templateQuantity > 0
                        ) {
                          setAlert(
                            "Please remove templates from your order before removing niches.",
                            "warning",
                          );
                          return;
                        }
                        await applyAddOnItem({
                          name: getItemName(row) as AddOnItemName,
                          quantity: 0,
                        });
                      }}
                    >
                      Remove add on
                    </LoadingButton>
                  </Grid>
                )}
              </TableCell>
              <TableCell align="right">
                ${Number(row.amount / 100).toFixed(2)}
              </TableCell>
            </TableRow>
          ))}

          {templateQuantity === 0 && displayVaBundle && (
            <TableRow>
              <TableCell component="th" scope="row">
                Bring your own template
              </TableCell>

              <TableCell align="right">$0.00</TableCell>
            </TableRow>
          )}

          {displayVaBundle && (
            <TableRow>
              <TableCell component="th" scope="row">
                Pitch up to 2 niches at once
              </TableCell>

              <TableCell align="right">$0.00</TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell component="th" scope="row">
              Tax{" "}
              {requiresBillingAddress
                ? "(Enter Address To Calculate)"
                : "(Based On Your Location)"}
            </TableCell>
            <TableCell align="right">
              {invoice?.total_taxes?.[0]?.amount ? (
                <>
                  {" "}
                  ${Number(invoice?.total_taxes?.[0]?.amount / 100).toFixed(2)}
                </>
              ) : (
                "$0.00"
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {hasDiscount || negativeBalance ? (
        <Grid item xs={12} sx={{ mt: 2, px: 2 }}>
          <strong>Adjustments</strong>
        </Grid>
      ) : (
        <></>
      )}

      {invoice?.total_discount_amounts?.map((row, index) => (
        <Grid
          item
          xs={12}
          key={index}
          sx={{ px: 2, py: 1, fontSize: 12 }}
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            disableElevation
            size="small"
            variant="contained"
            sx={{ fontSize: 10 }}
            onClick={removePromoCode}
            // For discounts like "First Month Virtual Assistant Free"
            // we don't want to allow them to remove it. But they can
            // remove their own promo code.
            disabled={!invoice?.discounts?.[index]?.promotion_code}
          >
            <Box sx={{ mr: 1 }} component="i" className="fa-solid fa-tag" />
            {invoice?.discounts?.[index]?.coupon?.name}

            {invoice?.discounts?.[index]?.promotion_code && (
              <Box
                component="i"
                className="fa-regular fa-xmark"
                sx={{ ml: 1 }}
              />
            )}
          </Button>
          <Box sx={{ color: grey[500] }}>
            -${Number(row.amount / 100).toFixed(2)}
          </Box>
        </Grid>
      ))}

      {invoice && invoice?.starting_balance !== 0 && (
        <Grid
          item
          xs={12}
          sx={{ px: 2, py: 1, fontSize: 12 }}
          container
          justifyContent="space-between"
        >
          <Box>Credit Balance </Box>
          <Box sx={{ color: grey[500] }}>
            -${Number(Math.abs(invoice?.starting_balance / 100)).toFixed(2)}
          </Box>
        </Grid>
      )}

      <Table
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableBody>
          <TableRow>
            {(invoice?.next_payment_attempt || invoice?.period_start) && (
              <TableCell
                component="th"
                scope="row"
                sx={{ fontSize: 14, color: "black" }}
              >
                <strong>
                  Total due on{" "}
                  {formatEpochTimestamp({
                    epochTimestamp:
                      invoice?.next_payment_attempt || invoice?.period_start,
                    addYear: true,
                  })}{" "}
                </strong>
              </TableCell>
            )}
            <TableCell align="right" sx={{ fontSize: 15, color: "black" }}>
              <strong>
                <>
                  ${Number((invoice?.amount_due || 0) / 100).toFixed(2)}{" "}
                  <Box component="span" sx={{ textTransform: "uppercase" }}>
                    {invoice?.currency}
                  </Box>
                </>
              </strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {isMobileScreen ? (
        <Grid
          container
          alignItems="center"
          justifyContent={"space-between"}
          sx={{ mt: 2 }}
        >
          <Grid item xs={3}>
            {displayVaBundle && (
              <Button
                disableElevation
                variant="contained"
                size="small"
                sx={{ textTransform: "none", fontSize: 10 }}
                onClick={handleOpenAddOns}
              >
                + Add Ons
              </Button>
            )}
          </Grid>
          <Grid container item xs justifyContent="flex-end">
            {!hasDiscount && plan !== AddOnItemName.support_call && (
              <PromotionalCode productId={productId} />
            )}
          </Grid>
          <Grid item xs={12}>
            {displayPartnershipBundle && renderBundle()}
          </Grid>
          <Dialog open={openAddOns} onClose={() => setOpenAddOns(false)}>
            <DialogTitle>Purchase Add Ons</DialogTitle>
            <DialogContent>
              Click <strong>+ Add </strong> when you want to add the item to the
              invoice.
              {renderBundle()}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setOpenAddOns(false)}>Done</Button>
            </DialogActions>
          </Dialog>
        </Grid>
      ) : (
        <>
          {!hasDiscount && plan !== AddOnItemName.support_call && (
            <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
              <PromotionalCode productId={productId} />
            </Grid>
          )}
          {renderBundle()}
        </>
      )}
    </Box>
  );
};

export default InvoicePreview;
